import { unzipSync, zipSync } from "fflate";
import { type AnnotationRecord, state } from "../state";
import { camera, controls, renderer, scene } from "../viewer/scene";
import { setStatus } from "../ui/status";
import { sanitizeAnnotationHtml } from "../utils/html";
import {
  persistAnnotations,
  refreshAnnotationMarkers,
  renderAnnotationList,
  updateAnnotationUi,
} from "./annotations";

/**
 * BCF 2.x (BIM Collaboration Format) — стандарт обмена замечаниями BIM.
 * Архив-zip с расширением `.bcfzip`/`.bcf` содержит:
 *   - `bcf.version`   — XML с версией формата (2.1)
 *   - `<TopicGUID>/markup.bcf`     — Header + Topic + Comments + Viewpoints
 *   - `<TopicGUID>/viewpoint.bcfv` — VisualizationInfo (CameraViewPoint + ClippingPlanes)
 *   - `<TopicGUID>/snapshot.png`   — превью текущего вида
 *
 * Мы экспортируем каждое локальное примечание `AnnotationRecord` в отдельный
 * Topic. Так как координаты камеры/clipping у нас одни на момент экспорта,
 * все viewpoints получают одинаковый VisualizationInfo. При импорте, наоборот,
 * читаем Title/Description и пытаемся восстановить элементы по GlobalId.
 *
 * NB: Мы используем приоритеты Topic.Priority вместо нашего сленга (low/high и
 * т.п.) — конвертация в обе стороны через PRIORITY_TO_BCF / PRIORITY_FROM_BCF.
 */

const PRIORITY_TO_BCF: Record<string, string> = {
  suspended: "Low",
  low: "Low",
  normal: "Normal",
  high: "High",
  critical: "Critical",
};

const PRIORITY_FROM_BCF: Record<string, string> = {
  low: "low",
  normal: "normal",
  medium: "normal",
  high: "high",
  critical: "critical",
};

function xmlEscape(value: string | number | undefined | null): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function isoNow(): string {
  return new Date().toISOString();
}

function generateGuid(): string {
  // Лёгкий UUID v4 на crypto.getRandomValues — стандартного crypto.randomUUID
  // нет в старых браузерах, делаем сами.
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function htmlToPlainText(html: string): string {
  const tmp = document.createElement("div");
  tmp.innerHTML = sanitizeAnnotationHtml(html || "");
  return (tmp.textContent || tmp.innerText || "").replace(/\u00a0/g, " ").trim();
}

function buildBcfVersionXml(): string {
  return (
    '<?xml version="1.0" encoding="UTF-8"?>\n' +
    '<Version VersionId="2.1" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">\n' +
    "  <DetailedVersion>2.1</DetailedVersion>\n" +
    "</Version>\n"
  );
}

function buildMarkupXml(opts: {
  topicGuid: string;
  viewpointGuid: string;
  title: string;
  description: string;
  priority: string;
  creationDate: string;
  components: number[];
}): string {
  const bcfPriority = PRIORITY_TO_BCF[opts.priority] || "Normal";
  const componentsXml = opts.components.length
    ? "      <ViewSetupHints SpacesVisible=\"true\" SpaceBoundariesVisible=\"false\" OpeningsVisible=\"false\"/>\n" +
      "      <Selection>\n" +
      opts.components
        .map(
          (id) =>
            `        <Component IfcGuid="" OriginatingSystem="IFC Web Viewer" AuthoringToolId="${xmlEscape(
              id,
            )}"/>\n`,
        )
        .join("") +
      "      </Selection>\n"
    : "";
  return (
    '<?xml version="1.0" encoding="UTF-8"?>\n' +
    `<Markup xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">\n` +
    "  <Header>\n" +
    `    <File IfcProject="" IfcSpatialStructureElement="" isExternal="true">\n` +
    `      <Filename>${xmlEscape(state.currentIfcFileKey || "model.ifc")}</Filename>\n` +
    `      <Date>${xmlEscape(opts.creationDate)}</Date>\n` +
    "    </File>\n" +
    "  </Header>\n" +
    `  <Topic Guid="${xmlEscape(opts.topicGuid)}" TopicType="Issue" TopicStatus="Open">\n` +
    `    <Title>${xmlEscape(opts.title)}</Title>\n` +
    `    <Priority>${xmlEscape(bcfPriority)}</Priority>\n` +
    `    <CreationDate>${xmlEscape(opts.creationDate)}</CreationDate>\n` +
    `    <CreationAuthor>IFC Web Viewer</CreationAuthor>\n` +
    `    <Description>${xmlEscape(opts.description)}</Description>\n` +
    "  </Topic>\n" +
    `  <Viewpoints Guid="${xmlEscape(opts.viewpointGuid)}">\n` +
    "    <Viewpoint>viewpoint.bcfv</Viewpoint>\n" +
    "    <Snapshot>snapshot.png</Snapshot>\n" +
    "  </Viewpoints>\n" +
    componentsXml +
    "</Markup>\n"
  );
}

function buildViewpointXml(viewpointGuid: string): string {
  const px = camera.position.x.toFixed(6);
  const py = camera.position.y.toFixed(6);
  const pz = camera.position.z.toFixed(6);
  const tx = controls.target.x.toFixed(6);
  const ty = controls.target.y.toFixed(6);
  const tz = controls.target.z.toFixed(6);
  // forward = target - position
  const fx = (controls.target.x - camera.position.x).toFixed(6);
  const fy = (controls.target.y - camera.position.y).toFixed(6);
  const fz = (controls.target.z - camera.position.z).toFixed(6);
  const ux = camera.up.x.toFixed(6);
  const uy = camera.up.y.toFixed(6);
  const uz = camera.up.z.toFixed(6);

  const clippingPlanesXml = renderer.clippingPlanes
    ? renderer.clippingPlanes
        .map(
          (plane) =>
            "    <ClippingPlane>\n" +
            `      <Location X="${(plane.normal.x * -plane.constant).toFixed(6)}" ` +
            `Y="${(plane.normal.y * -plane.constant).toFixed(6)}" ` +
            `Z="${(plane.normal.z * -plane.constant).toFixed(6)}"/>\n` +
            `      <Direction X="${plane.normal.x.toFixed(6)}" Y="${plane.normal.y.toFixed(6)}" ` +
            `Z="${plane.normal.z.toFixed(6)}"/>\n` +
            "    </ClippingPlane>\n",
        )
        .join("")
    : "";

  return (
    '<?xml version="1.0" encoding="UTF-8"?>\n' +
    `<VisualizationInfo xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" Guid="${xmlEscape(
      viewpointGuid,
    )}">\n` +
    "  <PerspectiveCamera>\n" +
    `    <CameraViewPoint X="${px}" Y="${py}" Z="${pz}"/>\n` +
    `    <CameraDirection X="${fx}" Y="${fy}" Z="${fz}"/>\n` +
    `    <CameraUpVector X="${ux}" Y="${uy}" Z="${uz}"/>\n` +
    `    <FieldOfView>${(camera.fov || 60).toFixed(2)}</FieldOfView>\n` +
    "  </PerspectiveCamera>\n" +
    (clippingPlanesXml ? "  <ClippingPlanes>\n" + clippingPlanesXml + "  </ClippingPlanes>\n" : "") +
    "  <Components>\n" +
    `    <ViewSetupHints SpacesVisible="true" SpaceBoundariesVisible="false" OpeningsVisible="false"/>\n` +
    `    <CameraTarget X="${tx}" Y="${ty}" Z="${tz}"/>\n` +
    "  </Components>\n" +
    "</VisualizationInfo>\n"
  );
}

async function getCurrentSnapshotPng(): Promise<Uint8Array | null> {
  try {
    renderer.render(scene, camera);
    const dataUrl = renderer.domElement.toDataURL("image/png");
    const base64 = dataUrl.split(",")[1] || "";
    const bin = atob(base64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      out[i] = bin.charCodeAt(i);
    }
    return out;
  } catch (_) {
    return null;
  }
}

/**
 * Экспортирует все локальные примечания (`state.annotations`) в BCF 2.1 zip.
 * Если примечаний нет — статус-сообщение и выход.
 */
export async function exportAnnotationsToBcf(): Promise<void> {
  if (state.annotations.length === 0) {
    setStatus("Нечего экспортировать в BCF: список примечаний пуст.", true);
    return;
  }
  const snapshot = await getCurrentSnapshotPng();
  const creationDate = isoNow();
  const files: Record<string, Uint8Array> = {
    "bcf.version": new TextEncoder().encode(buildBcfVersionXml()),
  };

  for (const ann of state.annotations) {
    const topicGuid = generateGuid();
    const viewpointGuid = generateGuid();
    const folder = topicGuid;
    const description = htmlToPlainText(ann.descriptionHtml);
    files[`${folder}/markup.bcf`] = new TextEncoder().encode(
      buildMarkupXml({
        topicGuid,
        viewpointGuid,
        title: ann.title || `Topic ${ann.id}`,
        description,
        priority: ann.priority,
        creationDate,
        components: ann.elementIds,
      }),
    );
    files[`${folder}/viewpoint.bcfv`] = new TextEncoder().encode(buildViewpointXml(viewpointGuid));
    if (snapshot) {
      files[`${folder}/snapshot.png`] = snapshot;
    }
  }

  const zipped = zipSync(files);
  // Копируем в свежий ArrayBuffer — fflate возвращает Uint8Array<ArrayBufferLike>,
  // strict-режим TS отказывает в передаче такого массива в конструктор Blob.
  const zippedCopy = new Uint8Array(zipped.byteLength);
  zippedCopy.set(zipped);
  const blob = new Blob([zippedCopy.buffer as ArrayBuffer], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const stamp = creationDate.replace(/[:.]/g, "-");
  a.download = `ifc-annotations-${stamp}.bcfzip`;
  a.click();
  URL.revokeObjectURL(url);
  setStatus(`BCF 2.1: экспортировано ${state.annotations.length} примечаний.`);
}

interface ParsedBcfTopic {
  title: string;
  description: string;
  priority: string;
}

function parseMarkupXml(xml: string): ParsedBcfTopic | null {
  try {
    const doc = new DOMParser().parseFromString(xml, "application/xml");
    if (doc.getElementsByTagName("parsererror").length > 0) {
      return null;
    }
    const topic = doc.getElementsByTagName("Topic")[0];
    if (!topic) {
      return null;
    }
    const title = topic.getElementsByTagName("Title")[0]?.textContent || "BCF Topic";
    const description = topic.getElementsByTagName("Description")[0]?.textContent || "";
    const bcfPriority = (topic.getElementsByTagName("Priority")[0]?.textContent || "Normal")
      .toLowerCase()
      .trim();
    const priority = PRIORITY_FROM_BCF[bcfPriority] || "normal";
    return { title: title.trim(), description: description.trim(), priority };
  } catch (_) {
    return null;
  }
}

/**
 * Импортирует BCFZIP: каждый Topic становится новым `AnnotationRecord`.
 * Привязка к элементам берётся из текущего выделения (если есть) — иначе
 * пусто. Это сознательное упрощение: восстановление по IfcGuid требует
 * запросов к web-ifc, которые могут быть медленными на больших моделях.
 */
export async function importAnnotationsFromBcf(file: File): Promise<void> {
  if (!file) {
    return;
  }
  setStatus(`BCF: распаковка ${file.name}…`);
  const buffer = new Uint8Array(await file.arrayBuffer());
  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(buffer);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    setStatus(`BCF: не удалось распаковать архив — ${message}`, true);
    return;
  }

  const markupNames = Object.keys(entries).filter((name) => /\/markup\.bcf$/i.test(name));
  if (markupNames.length === 0) {
    setStatus("BCF: в архиве не найдено ни одного Topic (markup.bcf).", true);
    return;
  }

  const elementIds = state.selectedExpressIds.size > 0 ? [...state.selectedExpressIds] : [];
  let imported = 0;
  for (const name of markupNames) {
    const xml = new TextDecoder("utf-8").decode(entries[name]);
    const parsed = parseMarkupXml(xml);
    if (!parsed) {
      continue;
    }
    const newId = state.annotationIdCounter++;
    const record: AnnotationRecord = {
      id: newId,
      title: parsed.title,
      priority: parsed.priority,
      deadline: "",
      descriptionHtml: sanitizeAnnotationHtml(
        parsed.description
          ? `<p>${parsed.description.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\n/g, "<br>")}</p>`
          : "",
      ),
      elementIds: [...elementIds].sort((a, b) => a - b),
    };
    state.annotations.push(record);
    imported += 1;
  }

  persistAnnotations();
  refreshAnnotationMarkers();
  updateAnnotationUi();
  renderAnnotationList();
  setStatus(
    `BCF: импортировано ${imported} примечаний (привязка к элементам — текущее выделение).`,
  );
}
